import {
  Bug,
  Wrench,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  CheckCircle,
  CircleCheck,
  FileText,
  CircleOff,
  Edit3,
  Undo2,
  EyeOff,
  Clock3,
  PlayCircle,
  ChevronsUp,
  Minus,
  ArrowUp,
  ArrowDown,
  PlusCircle,
  CheckSquare,
  HelpCircle,
  CircleDot,
  Code,
  Rocket,
  GitBranch,
  Cpu,
  Hand,
  Activity,
  Ban,
  SkipForward,
} from "lucide-react";
import React from "react";

/**
 * LabelPalette (single canonical file)
 * Each PaletteEntry exposes:
 *  - text, icon
 *  - solidClass  -> strong filled badge classes (bg + text)
 *  - liteClass   -> subtle chip classes (lighter bg + text)
 *  - textClass   -> text-only classes (no bg)
 *
 * All class strings use Tailwind-style utilities (matching your code).
 */

export type PaletteEntry = {
  text: string;
  icon: React.ReactNode;
  solidClass: string;
  liteClass: string;
  textClass: string;
};

export type DomainMap = Record<string, PaletteEntry>;

/* ---------- DEFECT_STATUS ---------- */
export const DEFECT_STATUS: DomainMap = {
  NEW: {
    text: "New",
    icon: <CircleDot size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-50 text-blue-700 border border-blue-200",
    textClass: "text-blue-600",
  },
  new: {
    text: "New",
    icon: <CircleDot size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-50 text-blue-700 border border-blue-200",
    textClass: "text-blue-600",
  },
  OPEN: {
    text: "Open",
    icon: <AlertTriangle size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-50 text-blue-700 border border-blue-200",
    textClass: "text-blue-600",
  },
  open: {
    text: "Open",
    icon: <AlertTriangle size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-50 text-blue-700 border border-blue-200",
    textClass: "text-blue-600",
  },
  IN_PROGRESS: {
    text: "In Progress",
    icon: <Wrench size={13} strokeWidth={2.5} />,
    solidClass: "bg-yellow-600 text-white",
    liteClass: "bg-amber-50 text-amber-700 border border-amber-200",
    textClass: "text-yellow-600",
  },
  "in-progress": {
    text: "In Progress",
    icon: <Wrench size={13} strokeWidth={2.5} />,
    solidClass: "bg-yellow-600 text-white",
    liteClass: "bg-amber-50 text-amber-700 border border-amber-200",
    textClass: "text-yellow-600",
  },
  RESOLVED: {
    text: "Resolved",
    icon: <CheckCircle2 size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-50 text-green-700 border border-green-200",
    textClass: "text-green-600",
  },
  resolved: {
    text: "Resolved",
    icon: <CheckCircle2 size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-50 text-green-700 border border-green-200",
    textClass: "text-green-600",
  },
  FIXED: {
    text: "Fixed",
    icon: <CheckCircle2 size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-50 text-green-700 border border-green-200",
    textClass: "text-green-600",
  },
  RETEST: {
    text: "Retest",
    icon: <Undo2 size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-50 text-orange-700 border border-orange-200",
    textClass: "text-orange-600",
  },
  CLOSED: {
    text: "Closed",
    icon: <XCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-600 text-white",
    liteClass: "bg-gray-50 text-gray-600 border border-gray-200",
    textClass: "text-gray-600",
  },
  closed: {
    text: "Closed",
    icon: <XCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-600 text-white",
    liteClass: "bg-gray-50 text-gray-600 border border-gray-200",
    textClass: "text-gray-600",
  },
  unknown: {
    text: "Unknown",
    icon: <AlertTriangle size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-800",
  },
};

/* ---------- EXECUTION_STATUS ---------- */
export const EXECUTION_STATUS: DomainMap = {
  STATUS_CREATED: {
    text: "Initiated",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-600 text-white",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-600",
  },
  STATUS_QUEUED: {
    text: "Queued",
    icon: <Clock3 size={13} strokeWidth={2.5} />,
    solidClass: "bg-yellow-600 text-white",
    liteClass: "bg-yellow-200 text-yellow-800",
    textClass: "text-yellow-600",
  },
  STATUS_PRE_FLIGHT: {
    text: "Pre-checking",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-100 text-blue-700",
    textClass: "text-blue-600",
  },
  STATUS_IN_PROGRESS: {
    text: "Running",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-700 text-white",
    liteClass: "bg-blue-200 text-blue-800",
    textClass: "text-blue-700",
  },
  STATUS_COMPLETED: {
    text: "Completed",
    icon: <CheckCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-100 text-green-800",
    textClass: "text-green-600",
  },
  created: {
    text: "Initiated",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-600 text-white",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-600",
  },
  queued: {
    text: "Queued",
    icon: <Clock3 size={13} strokeWidth={2.5} />,
    solidClass: "bg-yellow-600 text-white",
    liteClass: "bg-yellow-200 text-yellow-800",
    textClass: "text-yellow-600",
  },
  "in-progress": {
    text: "Running",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-700 text-white",
    liteClass: "bg-blue-200 text-blue-800",
    textClass: "text-blue-700",
  },
  completed: {
    text: "Completed",
    icon: <CheckCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-100 text-green-800",
    textClass: "text-green-600",
  },
  unknown: {
    text: "Unknown",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-800",
  },
};

/* ---------- TESTCASE_STATUS ---------- */
export const TESTCASE_STATUS: DomainMap = {
  IN_REVIEW: {
    text: "In Review",
    icon: <FileText size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-100 text-blue-800",
    textClass: "text-blue-600",
  },
  READY: {
    text: "Ready",
    icon: <CheckCircle2 size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-100 text-green-800",
    textClass: "text-green-600",
  },
  OBSOLETE: {
    text: "Obsolete",
    icon: <CircleOff size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-600",
  },
  DRAFT: {
    text: "Draft",
    icon: <Edit3 size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-200 text-orange-800",
    textClass: "text-orange-600",
  },
  REWORK: {
    text: "Rework",
    icon: <Undo2 size={13} strokeWidth={2.5} />,
    solidClass: "bg-red-600 text-white",
    liteClass: "bg-red-100 text-red-800",
    textClass: "text-red-600",
  },
  UNTESTED: {
    text: "Untested",
    icon: <EyeOff size={13} strokeWidth={2.5} />,
    solidClass: "bg-yellow-600 text-white",
    liteClass: "bg-yellow-200 text-yellow-800",
    textClass: "text-yellow-600",
  },
  unknown: {
    text: "Unknown",
    icon: <FileText size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-800",
  },
};

/* ---------- TEST_RESULT ---------- */
export const TEST_RESULT: DomainMap = {
  SUCCESS: {
    text: "Passed",
    icon: <CircleCheck size={14} strokeWidth={2} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-50 text-green-700",
    textClass: "text-green-600",
  },
  PASSED: {
    text: "Passed",
    icon: <CircleCheck size={14} strokeWidth={2} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-50 text-green-700",
    textClass: "text-green-600",
  },
  FAILURE: {
    text: "Failed",
    icon: <XCircle size={14} strokeWidth={2} />,
    solidClass: "bg-red-600 text-white",
    liteClass: "bg-red-50 text-red-700",
    textClass: "text-red-600",
  },
  ABORTED: {
    text: "Aborted",
    icon: <AlertTriangle size={14} strokeWidth={2} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-50 text-orange-700",
    textClass: "text-orange-600",
  },
  STOPPED: {
    text: "Stopped",
    icon: <CircleOff size={14} strokeWidth={2} />,
    solidClass: "bg-gray-500 text-white",
    liteClass: "bg-gray-100 text-gray-600",
    textClass: "text-gray-500",
  },
  QUEUED: {
    text: "Queued",
    icon: <Clock3 size={14} strokeWidth={2} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-50 text-blue-700",
    textClass: "text-blue-600",
  },
  NOT_EXECUTED: {
    text: "Not Executed",
    icon: <PlayCircle size={14} strokeWidth={2} />,
    solidClass: "bg-sky-500 text-white",
    liteClass: "bg-sky-50 text-sky-600",
    textClass: "text-sky-500",
  },
  BLOCK: {
    text: "Blocked",
    icon: <Ban size={14} strokeWidth={2} />,
    solidClass: "bg-indigo-500 text-white",
    liteClass: "bg-indigo-50 text-indigo-600",
    textClass: "text-indigo-500",
  },
  BLOCKED: {
    text: "Blocked",
    icon: <Ban size={14} strokeWidth={2} />,
    solidClass: "bg-indigo-500 text-white",
    liteClass: "bg-indigo-50 text-indigo-600",
    textClass: "text-indigo-500",
  },
  SKIP: {
    text: "Skipped",
    icon: <SkipForward size={14} strokeWidth={2} />,
    solidClass: "bg-amber-500 text-white",
    liteClass: "bg-amber-50 text-amber-600",
    textClass: "text-amber-500",
  },
  SKIPPED: {
    text: "Skipped",
    icon: <SkipForward size={14} strokeWidth={2} />,
    solidClass: "bg-amber-500 text-white",
    liteClass: "bg-amber-50 text-amber-600",
    textClass: "text-amber-500",
  },
  PROCESSING: {
    text: "In Process",
    icon: <Clock size={14} strokeWidth={2} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-50 text-blue-700",
    textClass: "text-blue-600",
  },
  unknown: {
    text: "Unknown",
    icon: <HelpCircle size={14} strokeWidth={2} />,
    solidClass: "bg-gray-400 text-white",
    liteClass: "bg-gray-100 text-gray-500",
    textClass: "text-gray-400",
  },
};

/* ---------- PRIORITY ---------- */

/**
 * Priority renders as the canonical P0–P3 vocabulary everywhere (matches the
 * cycles module). Word-form keys (Critical/High/Medium/Low + synonyms) and the
 * P-codes both resolve to the same P-code label, so a badge reads identically no
 * matter which spelling a backend endpoint returns:
 *   Critical → P0 · High → P1 · Medium → P2 · Low → P3
 */
const PRIORITY_P0: PaletteEntry = {
  text: "P0 - Critical",
  icon: <ChevronsUp size={16} strokeWidth={2.5} />,
  solidClass: "bg-red-700 text-white",
  liteClass: "bg-red-50 text-red-700 border border-red-200",
  textClass: "text-red-700",
};
const PRIORITY_P1: PaletteEntry = {
  text: "P1 - High",
  icon: <ArrowUp size={16} strokeWidth={2.5} />,
  solidClass: "bg-orange-600 text-white",
  liteClass: "bg-orange-50 text-orange-700 border border-orange-200",
  textClass: "text-orange-600",
};
const PRIORITY_P2: PaletteEntry = {
  text: "P2 - Medium",
  icon: <Minus size={16} strokeWidth={2.5} />,
  solidClass: "bg-blue-600 text-white",
  liteClass: "bg-blue-50 text-blue-700 border border-blue-200",
  textClass: "text-blue-600",
};
const PRIORITY_P3: PaletteEntry = {
  text: "P3 - Low",
  icon: <ArrowDown size={16} strokeWidth={2.5} />,
  solidClass: "bg-green-600 text-white",
  liteClass: "bg-green-50 text-green-700 border border-green-200",
  textClass: "text-green-600",
};

export const PRIORITY: DomainMap = {
  // P0 — Critical / Blocker / Urgent
  P0: PRIORITY_P0,
  p0: PRIORITY_P0,
  CRITICAL: PRIORITY_P0,
  critical: PRIORITY_P0,
  BLOCKER: PRIORITY_P0,
  blocker: PRIORITY_P0,
  URGENT: PRIORITY_P0,
  urgent: PRIORITY_P0,
  // P1 — High / Major
  P1: PRIORITY_P1,
  p1: PRIORITY_P1,
  HIGH: PRIORITY_P1,
  high: PRIORITY_P1,
  MAJOR: PRIORITY_P1,
  major: PRIORITY_P1,
  // P2 — Medium / Normal / Moderate
  P2: PRIORITY_P2,
  p2: PRIORITY_P2,
  MEDIUM: PRIORITY_P2,
  medium: PRIORITY_P2,
  NORMAL: PRIORITY_P2,
  normal: PRIORITY_P2,
  MODERATE: PRIORITY_P2,
  moderate: PRIORITY_P2,
  // P3 — Low / Minor / Trivial
  P3: PRIORITY_P3,
  p3: PRIORITY_P3,
  LOW: PRIORITY_P3,
  low: PRIORITY_P3,
  MINOR: PRIORITY_P3,
  minor: PRIORITY_P3,
  TRIVIAL: PRIORITY_P3,
  trivial: PRIORITY_P3,
  unknown: {
    text: "Unknown",
    icon: <Minus size={16} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-800",
  },
};

/* ---------- SEVERITY ---------- */
export const SEVERITY: DomainMap = {
  BLOCKER: {
    text: "Blocker",
    icon: <XCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-red-700 text-white",
    liteClass: "bg-red-50 text-red-700 border border-red-200",
    textClass: "text-red-700",
  },
  CRITICAL: {
    text: "Critical",
    icon: <AlertTriangle size={13} strokeWidth={2.5} />,
    solidClass: "bg-red-600 text-white",
    liteClass: "bg-red-50 text-red-700 border border-red-200",
    textClass: "text-red-600",
  },
  HIGH: {
    text: "High",
    icon: <AlertTriangle size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-50 text-orange-700 border border-orange-200",
    textClass: "text-orange-600",
  },
  MEDIUM: {
    text: "Medium",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-yellow-500 text-white",
    liteClass: "bg-amber-50 text-amber-700 border border-amber-200",
    textClass: "text-yellow-600",
  },
  LOW: {
    text: "Low",
    icon: <CheckCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-50 text-green-700 border border-green-200",
    textClass: "text-green-600",
  },
  MAJOR: {
    text: "Major",
    icon: <AlertTriangle size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-50 text-orange-700 border border-orange-200",
    textClass: "text-orange-600",
  },
  MINOR: {
    text: "Minor",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-yellow-200 text-gray-800",
    liteClass: "bg-amber-50 text-amber-700 border border-amber-200",
    textClass: "text-yellow-600",
  },
  TRIVIAL: {
    text: "Trivial",
    icon: <CheckCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-100 text-gray-800",
    liteClass: "bg-green-50 text-gray-800",
    textClass: "text-green-600",
  },
  unknown: {
    text: "Unknown",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-800",
  },
};
/* ---------- ENVIRONMENT ---------- */
export const ENVIRONMENT: DomainMap = {
  DEVELOPMENT: {
    text: "Development",
    icon: <Code size={13} strokeWidth={2.5} />,
    solidClass: "bg-red-700 text-white",
    liteClass: "bg-red-100 text-red-800",
    textClass: "text-red-700",
  },
  STAGING: {
    text: "Staging",
    icon: <GitBranch size={13} strokeWidth={2.5} />,
    solidClass: "bg-red-700 text-white",
    liteClass: "bg-red-100 text-red-800",
    textClass: "text-red-700",
  },
  PRODUCTION: {
    text: "Production",
    icon: <Rocket size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-100 text-orange-800",
    textClass: "text-orange-600",
  },
  QA: {
    text: "Qa",
    icon: <Bug size={13} strokeWidth={2.5} />,
    solidClass: "bg-yellow-500 text-white",
    liteClass: "bg-yellow-100 text-yellow-800",
    textClass: "text-yellow-600",
  },
};
/* ---------- DEFECT TYPES ---------- */

export const DEFECT_TYPE: DomainMap = {
  BUG: {
    text: "Bug",
    icon: <Bug size={13} strokeWidth={2.5} />,
    solidClass: "bg-red-600 text-white",
    liteClass: "bg-red-50 text-red-700 border border-red-200",
    textClass: "text-red-700",
  },
  FEATURE: {
    text: "Feature",
    icon: <PlusCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-50 text-blue-700 border border-blue-200",
    textClass: "text-blue-700",
  },
  TASK: {
    text: "Task",
    icon: <CheckSquare size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-50 text-green-700 border border-green-200",
    textClass: "text-green-700",
  },
  QUESTION: {
    text: "Question",
    icon: <HelpCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-purple-600 text-white",
    liteClass: "bg-purple-50 text-purple-700 border border-purple-200",
    textClass: "text-purple-700",
  },
  unknown: {
    text: "Unknown",
    icon: <HelpCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-800",
  },
};

/* ---------- TEST_TYPE (Professional UI Palette) ---------- */
export const TEST_TYPE: DomainMap = {
  AUTOMATION: {
    text: "Automation",
    icon: <Cpu size={14} strokeWidth={2.2} />,
    solidClass: "bg-indigo-600 text-white",
    liteClass: "bg-indigo-100 text-indigo-700", // indigo gives "tech / data" vibe
    textClass: "text-indigo-600",
  },
  AUTO: {
    text: "Automation",
    icon: <Cpu size={14} strokeWidth={2.2} />,
    solidClass: "bg-indigo-600 text-white",
    liteClass: "bg-indigo-100 text-indigo-700", // indigo gives "tech / data" vibe
    textClass: "text-indigo-600",
  },
  MANUAL: {
    text: "Manual",
    icon: <Hand size={14} strokeWidth={2.2} />,
    solidClass: "bg-amber-500 text-white",
    liteClass: "bg-amber-100 text-amber-700", // warm subtle amber
    textClass: "text-amber-600",
  },
  PERFORMANCE: {
    text: "Performance",
    icon: <Activity size={14} strokeWidth={2.2} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-100 text-orange-700", // slightly warm orange
    textClass: "text-orange-600",
  },
  unknown: {
    text: "Unknown",
    icon: <HelpCircle size={14} strokeWidth={2.2} />,
    solidClass: "bg-gray-400 text-white",
    liteClass: "bg-gray-100 text-gray-700",
    textClass: "text-gray-600",
  },
};

/* ---------- CYCLE_STATUS ---------- */
export const CYCLE_STATUS: DomainMap = {
  DRAFT: {
    text: "Draft",
    icon: <Edit3 size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-200 text-orange-800",
    textClass: "text-orange-600",
  },
  ACTIVE: {
    text: "Active",
    icon: <PlayCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-100 text-green-800",
    textClass: "text-green-600",
  },
  CLOSED: {
    text: "Closed",
    icon: <CircleOff size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-600",
  },
  unknown: {
    text: "Unknown",
    icon: <FileText size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-200 text-gray-800",
    liteClass: "bg-gray-100 text-gray-800",
    textClass: "text-gray-800",
  },
};

/* ---------- Helper: getBadgeProps (case-insensitive) ---------- */
export function getBadgeProps(
  domainMap: DomainMap,
  key?: string | null,
  fallbackKey = "unknown",
): PaletteEntry {
  if (!key) return domainMap[fallbackKey] ?? Object.values(domainMap)[0];
  if (domainMap[key]) return domainMap[key];
  const up = key.toUpperCase();
  if (domainMap[up]) return domainMap[up];
  const low = key.toLowerCase();
  if (domainMap[low]) return domainMap[low];
  return domainMap[fallbackKey] ?? Object.values(domainMap)[0];
}

/* ---------- Minimal Badge component (you can re-use this everywhere) ---------- */
export type BadgeProps = {
  domain: DomainMap;
  value?: string | null;
  variant?: "solid" | "lite" | "textOnly";
  isTextOnly?: boolean;
  showIcon?: boolean;
  size?: "xs" | "sm" | "md";
  className?: string;
  ringLite?: boolean;
};

const sizePadding: Record<string, string> = {
  xs: "px-2 py-0.5 text-xs",
  sm: "px-2 py-1 text-sm",
  md: "px-3 py-1.5 text-sm",
};

export const Badge: React.FC<BadgeProps> = ({
  domain,
  value,
  variant = "lite",
  isTextOnly = false,
  showIcon = true,
  size = "sm",
  className = "",
  ringLite = true,
}) => {
  const p = getBadgeProps(domain, value);
  const padding = sizePadding[size] ?? sizePadding.sm;

  const chosen =
    isTextOnly || variant === "textOnly"
      ? p.textClass
      : variant === "solid"
        ? p.solidClass
        : p.liteClass;

  const ring =
    variant === "lite" && ringLite ? " ring-1 ring-inset ring-gray-100" : "";

  if (isTextOnly || variant === "textOnly") {
    return (
      <span
        aria-label={p.text}
        className={`${p.textClass} ${padding} font-medium ${className}`}
      >
        {p.text}
      </span>
    );
  }

  return (
    <div
      aria-label={p.text}
      className={`${chosen} ${padding} inline-flex items-center gap-2 rounded ${ring} ${className}`}
    >
      {showIcon && <span className="inline-flex items-center">{p.icon}</span>}
      <span className="font-medium">{p.text}</span>
    </div>
  );
};

/* ---------- RUN_OUTCOME — normalized cycle run testing result ---------- */
export const RUN_OUTCOME: DomainMap = {
  PASSED: {
    text: "Passed",
    icon: <CheckCircle2 size={13} strokeWidth={2.5} />,
    solidClass: "bg-green-600 text-white",
    liteClass: "bg-green-50 text-green-700 border border-green-200",
    textClass: "text-green-600",
  },
  FAILED: {
    text: "Failed",
    icon: <XCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-red-600 text-white",
    liteClass: "bg-red-50 text-red-700 border border-red-200",
    textClass: "text-red-600",
  },
  PARTIAL: {
    text: "Partial",
    icon: <AlertTriangle size={13} strokeWidth={2.5} />,
    solidClass: "bg-amber-600 text-white",
    liteClass: "bg-amber-50 text-amber-700 border border-amber-200",
    textClass: "text-amber-600",
  },
  NOT_EXECUTED: {
    text: "Not Executed",
    icon: <CircleOff size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-500 text-white",
    liteClass: "bg-gray-50 text-gray-500 border border-gray-200",
    textClass: "text-gray-500",
  },
};

/* ---------- CYCLE_RUN_EXECUTION_STATUS — lifecycle of a run ---------- */
export const CYCLE_RUN_EXECUTION_STATUS: DomainMap = {
  QUEUED: {
    text: "Queued",
    icon: <Clock size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-500 text-white",
    liteClass: "bg-gray-50 text-gray-600 border border-gray-200",
    textClass: "text-gray-600",
  },
  RUNNING: {
    text: "Running",
    icon: <PlayCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-blue-600 text-white",
    liteClass: "bg-blue-50 text-blue-700 border border-blue-200",
    textClass: "text-blue-700",
  },
  COMPLETED: {
    text: "Completed",
    icon: <CheckCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-700 text-white",
    liteClass: "bg-gray-100 text-gray-700 border border-gray-300",
    textClass: "text-gray-700",
  },
  PASSED: {
    text: "Completed",
    icon: <CheckCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-700 text-white",
    liteClass: "bg-gray-100 text-gray-700 border border-gray-300",
    textClass: "text-gray-700",
  },
  FAILED: {
    text: "Completed",
    icon: <CheckCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-700 text-white",
    liteClass: "bg-gray-100 text-gray-700 border border-gray-300",
    textClass: "text-gray-700",
  },
  ABORTED: {
    text: "Aborted",
    icon: <Ban size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-50 text-orange-700 border border-orange-200",
    textClass: "text-orange-700",
  },
  STOPPED: {
    text: "Stopped",
    icon: <Hand size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-500 text-white",
    liteClass: "bg-orange-50 text-orange-600 border border-orange-200",
    textClass: "text-orange-600",
  },
  TIMED_OUT: {
    text: "Timed Out",
    icon: <Clock3 size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-50 text-orange-700 border border-orange-200",
    textClass: "text-orange-700",
  },
  SKIPPED: {
    text: "Skipped",
    icon: <SkipForward size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-400 text-white",
    liteClass: "bg-gray-50 text-gray-500 border border-gray-200",
    textClass: "text-gray-500",
  },
};

/* ---------- TERMINATION_REASON — why a run ended abnormally ---------- */
export const TERMINATION_REASON: DomainMap = {
  USER_STOP: {
    text: "User stop",
    icon: <Hand size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-500 text-white",
    liteClass: "bg-orange-50 text-orange-600 border border-orange-200",
    textClass: "text-orange-600",
  },
  HEARTBEAT_STALE: {
    text: "Server restart",
    icon: <Activity size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-50 text-orange-700 border border-orange-200",
    textClass: "text-orange-700",
  },
  TIMEOUT: {
    text: "Timed out",
    icon: <Clock3 size={13} strokeWidth={2.5} />,
    solidClass: "bg-orange-600 text-white",
    liteClass: "bg-orange-50 text-orange-700 border border-orange-200",
    textClass: "text-orange-700",
  },
  CRASH: {
    text: "Worker crash",
    icon: <AlertTriangle size={13} strokeWidth={2.5} />,
    solidClass: "bg-red-600 text-white",
    liteClass: "bg-red-50 text-red-700 border border-red-200",
    textClass: "text-red-700",
  },
  UNKNOWN: {
    text: "Unknown",
    icon: <HelpCircle size={13} strokeWidth={2.5} />,
    solidClass: "bg-gray-500 text-white",
    liteClass: "bg-gray-50 text-gray-500 border border-gray-200",
    textClass: "text-gray-500",
  },
};

/* ---------- default export (maps + helpers) ---------- */
const LabelPalette = {
  DEFECT_STATUS,
  EXECUTION_STATUS,
  TESTCASE_STATUS,
  TEST_RESULT,
  PRIORITY,
  SEVERITY,
  TEST_TYPE,
  DEFECT_TYPE,
  ENVIRONMENT,
  CYCLE_STATUS,
  RUN_OUTCOME,
  CYCLE_RUN_EXECUTION_STATUS,
  TERMINATION_REASON,
  getBadgeProps,
  Badge,
};

export default LabelPalette;
